import { closestPointOnSegment, haversineM, lineDistanceM } from "./geo.ts";
import type { LngLat, PieceGraph } from "./types.ts";

/** A few square kilometres. Larger outlines are rejected before any roads are copied. */
export const MAX_FILL_AREA_M2 = 4_000_000;
/** Resampled fill above this is rejected. */
export const MAX_FILL_POINTS = 5_000;
/** Bail before welding a huge tile dump. */
export const MAX_FILL_INPUT_VERTICES = 40_000;

export const DEFAULT_SPACING_M = 30;
export const MIN_SPACING_M = 10;
export const MAX_SPACING_M = 200;

/** Line ends and T-junctions closer than this become one point. */
export const JUNCTION_TOLERANCE_M = 6;
/** Snap a new fill point onto a point that was already in the graph. */
export const COINCIDE_TOLERANCE_M = 6;

const LAT_M = 111_320;
/** A dead end this close to another road is the same junction, not a separate piece. */
const DANGLING_JOIN_M = 12;
const BOUNDARY_EPS_M = 0.75;
/** Keep welded edges short so an erase removes a local piece of road. */
const WELD_SPLIT_M = 12;

export interface EraseResult {
  points: LngLat[];
  segments: Array<[number, number]>;
  start: number | null;
  selected: number | null;
}

export interface ResampledRoads {
  graph: PieceGraph;
  /** Welded-edge indexes covered by each resampled segment. */
  covers: number[][];
}

interface Local {
  x: number;
  y: number;
}

interface MarkedLine {
  coords: LngLat[];
  junction: boolean[];
}

export function clampSpacing(meters: number): number {
  if (!Number.isFinite(meters)) return DEFAULT_SPACING_M;
  return Math.max(MIN_SPACING_M, Math.min(MAX_SPACING_M, meters));
}

export function polygonAreaM2(ring: readonly LngLat[]): number {
  const clean = stripRing(ring);
  if (clean.length < 3) return 0;
  const origin = clean[0];
  let sum = 0;
  for (let i = 0; i < clean.length; i++) {
    const a = project(clean[i], origin);
    const b = project(clean[(i + 1) % clean.length], origin);
    sum += a.x * b.y - b.x * a.y;
  }
  return Math.abs(sum) / 2;
}

export function pointInPolygon(p: LngLat, ring: readonly LngLat[]): boolean {
  const clean = stripRing(ring);
  if (clean.length < 3) return false;
  if (onRing(p, clean, BOUNDARY_EPS_M)) return true;
  const origin = clean[0];
  const pt = project(p, origin);
  let inside = false;
  for (let i = 0, j = clean.length - 1; i < clean.length; j = i++) {
    const a = project(clean[i], origin);
    const b = project(clean[j], origin);
    const crosses = a.y > pt.y !== b.y > pt.y;
    if (!crosses) continue;
    const x = ((b.x - a.x) * (pt.y - a.y)) / (b.y - a.y) + a.x;
    if (pt.x < x) inside = !inside;
  }
  return inside;
}

/**
 * Keep the parts of each polyline that sit inside the ring.
 * The ring is open: the first point is not repeated at the end.
 */
export function clipLinesToPolygon(
  lines: readonly (readonly LngLat[])[],
  ring: readonly LngLat[],
): LngLat[][] {
  const clean = stripRing(ring);
  if (clean.length < 3) return [];
  const out: LngLat[][] = [];
  for (const line of lines) {
    let current: LngLat[] | null = null;
    const flushOutside = (): void => {
      current = null;
    };
    for (let i = 1; i < line.length; i++) {
      const pieces = clipSegment(line[i - 1], line[i], clean);
      if (pieces.length === 0) {
        flushOutside();
        continue;
      }
      for (const piece of pieces) {
        if (piece.length < 2) continue;
        if (current && haversineM(current[current.length - 1], piece[0]) < 0.2) {
          current.push(...piece.slice(1));
        } else {
          current = piece.map(clone);
          out.push(current);
        }
      }
      const last = pieces[pieces.length - 1];
      const end = last[last.length - 1];
      if (!end || haversineM(end, line[i]) > 0.2) flushOutside();
    }
  }
  return out.filter((line) => line.length >= 2 && lineDistanceM(line) > 0.4);
}

/**
 * Keep the parts of each polyline that sit inside the brush stroke.
 * One sample is a disk; consecutive samples are stadiums of that radius.
 */
export function clipLinesToStroke(
  lines: readonly (readonly LngLat[])[],
  samples: readonly LngLat[],
  radiusM: number,
): LngLat[][] {
  if (samples.length === 0 || !Number.isFinite(radiusM) || radiusM <= 0) return [];
  const out: LngLat[][] = [];
  for (const line of lines) {
    let current: LngLat[] | null = null;
    const flush = (): void => {
      current = null;
    };
    for (let i = 1; i < line.length; i++) {
      const pieces = clipSegmentToStroke(line[i - 1], line[i], samples, radiusM);
      if (pieces.length === 0) {
        flush();
        continue;
      }
      for (const piece of pieces) {
        if (piece.length < 2) continue;
        if (current && haversineM(current[current.length - 1], piece[0]) < 0.2) {
          current.push(...piece.slice(1));
        } else {
          current = piece.map(clone);
          out.push(current);
        }
      }
      const last = pieces[pieces.length - 1];
      const end = last[last.length - 1];
      if (!end || haversineM(end, line[i]) > 0.2) flush();
    }
  }
  return out.filter((line) => line.length >= 2 && lineDistanceM(line) > 0.4);
}

function clipSegmentToStroke(
  a: LngLat,
  b: LngLat,
  samples: readonly LngLat[],
  radiusM: number,
): LngLat[][] {
  const intervals: Array<[number, number]> = [];
  for (let i = 0; i < samples.length; i++) {
    intervals.push(...diskTIntervals(a, b, samples[i], radiusM));
    if (i > 0) intervals.push(...capsuleBandTIntervals(a, b, samples[i - 1], samples[i], radiusM));
  }
  return piecesFromTIntervals(a, b, mergeTIntervals(intervals));
}

/** t in [0, 1] where the segment sits inside the disk. */
function diskTIntervals(a: LngLat, b: LngLat, center: LngLat, radiusM: number): Array<[number, number]> {
  const A = project(a, center);
  const B = project(b, center);
  const dx = B.x - A.x;
  const dy = B.y - A.y;
  const qa = dx * dx + dy * dy;
  const qb = 2 * (A.x * dx + A.y * dy);
  const qc = A.x * A.x + A.y * A.y - radiusM * radiusM;
  if (qa < 1e-12) return qc <= 0 ? [[0, 1]] : [];
  const disc = qb * qb - 4 * qa * qc;
  if (disc < 0) return [];
  const root = Math.sqrt(disc);
  const t0 = (-qb - root) / (2 * qa);
  const t1 = (-qb + root) / (2 * qa);
  const lo = Math.max(0, Math.min(t0, t1));
  const hi = Math.min(1, Math.max(t0, t1));
  if (hi - lo <= 1e-8) return [];
  return [[lo, hi]];
}

/**
 * Interior of the stadium between two samples: distance to the sample
 * segment is ≤ radius, and the closest point is not an endpoint cap
 * (those caps are the disks).
 */
function capsuleBandTIntervals(
  a: LngLat,
  b: LngLat,
  c: LngLat,
  d: LngLat,
  radiusM: number,
): Array<[number, number]> {
  const C = project(c, c);
  const D = project(d, c);
  const A = project(a, c);
  const B = project(b, c);
  const sx = D.x - C.x;
  const sy = D.y - C.y;
  const len2 = sx * sx + sy * sy;
  if (len2 < 1e-12) return [];
  const len = Math.sqrt(len2);
  const abx = B.x - A.x;
  const aby = B.y - A.y;
  const s0 = (A.x * sx + A.y * sy) / len2;
  const ds = (abx * sx + aby * sy) / len2;
  const alpha = A.x * sy - A.y * sx;
  const beta = abx * sy - aby * sx;
  const limit = radiusM * len;
  const band = absLinearLeq(alpha, beta, limit);
  const along = ds === 0
    ? (s0 >= 0 && s0 <= 1 ? [[0, 1] as [number, number]] : [])
    : intersectUnit([[(0 - s0) / ds, (1 - s0) / ds].sort((left, right) => left - right) as [number, number]]);
  return intersectIntervalLists(band, along);
}

function absLinearLeq(alpha: number, beta: number, limit: number): Array<[number, number]> {
  if (Math.abs(beta) < 1e-12) return Math.abs(alpha) <= limit ? [[0, 1]] : [];
  const tLo = (-limit - alpha) / beta;
  const tHi = (limit - alpha) / beta;
  const lo = Math.min(tLo, tHi);
  const hi = Math.max(tLo, tHi);
  return intersectUnit([[lo, hi]]);
}

function intersectUnit(ranges: Array<[number, number]>): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  for (const [a, b] of ranges) {
    const lo = Math.max(0, Math.min(a, b));
    const hi = Math.min(1, Math.max(a, b));
    if (hi - lo > 1e-8) out.push([lo, hi]);
  }
  return out;
}

function intersectIntervalLists(
  left: Array<[number, number]>,
  right: Array<[number, number]>,
): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  for (const [a0, a1] of left) {
    for (const [b0, b1] of right) {
      const lo = Math.max(a0, b0);
      const hi = Math.min(a1, b1);
      if (hi - lo > 1e-8) out.push([lo, hi]);
    }
  }
  return out;
}

function mergeTIntervals(intervals: Array<[number, number]>): Array<[number, number]> {
  if (intervals.length === 0) return [];
  const sorted = intervals
    .map(([a, b]) => [Math.min(a, b), Math.max(a, b)] as [number, number])
    .sort((left, right) => left[0] - right[0]);
  const out: Array<[number, number]> = [sorted[0]];
  for (let i = 1; i < sorted.length; i++) {
    const cur = sorted[i];
    const last = out[out.length - 1];
    if (cur[0] <= last[1] + 1e-6) last[1] = Math.max(last[1], cur[1]);
    else out.push([cur[0], cur[1]]);
  }
  return out;
}

function piecesFromTIntervals(a: LngLat, b: LngLat, intervals: Array<[number, number]>): LngLat[][] {
  const parts: LngLat[][] = [];
  for (const [t0, t1] of intervals) {
    const lo = Math.max(0, t0);
    const hi = Math.min(1, t1);
    if (hi - lo <= 1e-8) continue;
    const p0 = lerp(a, b, lo);
    const p1 = lerp(a, b, hi);
    if (haversineM(p0, p1) < 0.05) continue;
    parts.push([p0, p1]);
  }
  return parts;
}

/**
 * Collapse exact tile copies, split T-junctions, and share one point where
 * line ends meet within `toleranceM`. Interior shape points stay put so a
 * curve is not pulled into a nearby junction.
 */
export function weldRoadLines(
  lines: readonly (readonly LngLat[])[],
  toleranceM = JUNCTION_TOLERANCE_M,
): PieceGraph {
  const cleaned = lines.map(cleanLine).filter((line) => line.length >= 2);
  const unique = dedupeExact(cleaned);
  const marked = insertTJunctions(unique, toleranceM);
  return joinDanglingEnds(subdivideLongEdges(buildGraph(marked, toleranceM), WELD_SPLIT_M), DANGLING_JOIN_M);
}

/**
 * Walk each chain between intersections and dead ends.
 * Samples land every `spacingM`; chain ends always stay.
 * A loop with no junction keeps one anchor point.
 */
export function resampleRoadGraph(graph: PieceGraph, spacingM: number): ResampledRoads {
  const spacing = clampSpacing(spacingM);
  const points: LngLat[] = [];
  const segments: Array<[number, number]> = [];
  const covers: number[][] = [];
  const fixedOut = new Map<number, number>();
  for (const chain of buildChains(graph)) {
    appendChain(graph, chain, spacing, points, segments, covers, fixedOut);
  }
  return { graph: { points, segments, start: null }, covers };
}

/**
 * Delete segments and drop points that no longer touch anything.
 * `keepIsolated` preserves a degree-0 point (used when a slider rebuild
 * should not throw away the creator's own points).
 */
export function eraseSegments(
  points: readonly LngLat[],
  segments: ReadonlyArray<readonly [number, number]>,
  removeIndexes: readonly number[],
  start: number | null = null,
  selected: number | null = null,
  keepIsolated?: (index: number) => boolean,
): EraseResult {
  const drop = new Set(removeIndexes);
  const kept = segments.filter((_, index) => !drop.has(index));
  const degree = new Array<number>(points.length).fill(0);
  for (const [a, b] of kept) {
    if (a >= 0 && a < points.length) degree[a] += 1;
    if (b >= 0 && b < points.length) degree[b] += 1;
  }
  const map = new Array<number>(points.length).fill(-1);
  const nextPoints: LngLat[] = [];
  for (let i = 0; i < points.length; i++) {
    if (degree[i] === 0 && !keepIsolated?.(i)) continue;
    map[i] = nextPoints.length;
    nextPoints.push(clone(points[i]));
  }
  const nextSegments: Array<[number, number]> = [];
  for (const [a, b] of kept) {
    const na = map[a];
    const nb = map[b];
    if (na == null || nb == null || na < 0 || nb < 0) continue;
    addUndirected(nextSegments, na, nb);
  }
  const remap = (index: number | null): number | null => {
    if (index == null || index < 0 || index >= map.length) return null;
    const next = map[index];
    return next >= 0 ? next : null;
  };
  return {
    points: nextPoints,
    segments: nextSegments,
    start: remap(start),
    selected: remap(selected),
  };
}

/** Drop welded edges and the points that only existed for them. */
export function exciseWeldedEdges(
  graph: PieceGraph,
  remove: readonly number[],
): { graph: PieceGraph; segmentMap: number[] } {
  const drop = new Set(remove);
  const segmentMap: number[] = [];
  const kept: Array<[number, number]> = [];
  for (let i = 0; i < graph.segments.length; i++) {
    if (drop.has(i)) {
      segmentMap.push(-1);
      continue;
    }
    segmentMap.push(kept.length);
    kept.push([graph.segments[i][0], graph.segments[i][1]]);
  }
  const cleaned = eraseSegments(graph.points, kept, [], graph.start, null);
  return {
    graph: { points: cleaned.points, segments: cleaned.segments, start: cleaned.start },
    segmentMap,
  };
}

/**
 * Remove segments that belong to the previous fill, then drop fill-only
 * points that nothing else uses. Creator points stay, including isolates.
 */
export function subtractFilledRoads(
  current: PieceGraph,
  basePoints: readonly LngLat[],
  previous: PieceGraph,
  toleranceM = COINCIDE_TOLERANCE_M,
): PieceGraph {
  const drop: number[] = [];
  for (let i = 0; i < current.segments.length; i++) {
    const [a, b] = current.segments[i];
    const pa = current.points[a];
    const pb = current.points[b];
    if (!pa || !pb) continue;
    if (matchesSegment(pa, pb, previous, toleranceM)) drop.push(i);
  }
  const result = eraseSegments(current.points, current.segments, drop, current.start, null, (index) => {
    const point = current.points[index];
    if (!point) return false;
    const wasFill = previous.points.some((fillPoint) => haversineM(fillPoint, point) <= toleranceM);
    if (!wasFill) return true;
    return basePoints.some((basePoint) => haversineM(basePoint, point) <= toleranceM);
  });
  return { points: result.points, segments: result.segments, start: result.start };
}

/**
 * Append `extra` onto `base`. A new point within `toleranceM` of a base
 * point reuses that point. Fill points are not collapsed into each other.
 */
export function mergeGraphs(
  base: PieceGraph,
  extra: PieceGraph,
  toleranceM = COINCIDE_TOLERANCE_M,
): PieceGraph {
  const points = base.points.map(clone);
  const segments = base.segments.map(([a, b]) => [a, b] as [number, number]);
  const mapIndex = extra.points.map((point) => nearestIndex(base.points, point, toleranceM));
  for (let i = 0; i < extra.points.length; i++) {
    if (mapIndex[i] >= 0) continue;
    mapIndex[i] = points.length;
    points.push(clone(extra.points[i]));
  }
  for (const [a, b] of extra.segments) {
    const ia = mapIndex[a];
    const ib = mapIndex[b];
    if (ia == null || ib == null || ia < 0 || ib < 0) continue;
    addUndirected(segments, ia, ib);
  }
  return { points, segments, start: base.start };
}

export function endsNear(a1: LngLat, b1: LngLat, a2: LngLat, b2: LngLat, toleranceM: number): boolean {
  const forward = haversineM(a1, a2) <= toleranceM && haversineM(b1, b2) <= toleranceM;
  const reverse = haversineM(a1, b2) <= toleranceM && haversineM(b1, a2) <= toleranceM;
  return forward || reverse;
}

function matchesSegment(a: LngLat, b: LngLat, previous: PieceGraph, toleranceM: number): boolean {
  for (const [i, j] of previous.segments) {
    const pa = previous.points[i];
    const pb = previous.points[j];
    if (!pa || !pb) continue;
    if (endsNear(a, b, pa, pb, toleranceM)) return true;
  }
  return false;
}

function clipSegment(a: LngLat, b: LngLat, ring: LngLat[]): LngLat[][] {
  const ts = [0, 1];
  for (let i = 0; i < ring.length; i++) {
    const hit = segmentIntersectionT(a, b, ring[i], ring[(i + 1) % ring.length]);
    if (hit == null || hit <= 1e-8 || hit >= 1 - 1e-8) continue;
    ts.push(hit);
  }
  ts.sort((left, right) => left - right);
  const unique: number[] = [];
  for (const t of ts) {
    if (unique.length === 0 || t - unique[unique.length - 1] > 1e-6) unique.push(t);
  }
  const parts: LngLat[][] = [];
  let current: LngLat[] | null = null;
  for (let i = 1; i < unique.length; i++) {
    const t0 = unique[i - 1];
    const t1 = unique[i];
    if (t1 - t0 <= 1e-8) continue;
    const mid = lerp(a, b, (t0 + t1) / 2);
    if (!pointInPolygon(mid, ring)) {
      current = null;
      continue;
    }
    const p0 = lerp(a, b, t0);
    const p1 = lerp(a, b, t1);
    if (haversineM(p0, p1) < 0.05) continue;
    if (current) current.push(p1);
    else {
      current = [p0, p1];
      parts.push(current);
    }
  }
  return parts;
}

function segmentIntersectionT(a: LngLat, b: LngLat, c: LngLat, d: LngLat): number | null {
  const hit = segmentIntersectionTU(a, b, c, d);
  return hit ? hit.t : null;
}

function segmentIntersectionTU(
  a: LngLat,
  b: LngLat,
  c: LngLat,
  d: LngLat,
): { t: number; u: number; point: LngLat } | null {
  const origin = a;
  const A = project(a, origin);
  const B = project(b, origin);
  const C = project(c, origin);
  const D = project(d, origin);
  const rx = B.x - A.x;
  const ry = B.y - A.y;
  const sx = D.x - C.x;
  const sy = D.y - C.y;
  const denom = rx * sy - ry * sx;
  if (Math.abs(denom) < 1e-6) return null;
  const qpx = C.x - A.x;
  const qpy = C.y - A.y;
  const t = (qpx * sy - qpy * sx) / denom;
  const u = (qpx * ry - qpy * rx) / denom;
  if (t < -1e-8 || t > 1 + 1e-8 || u < -1e-8 || u > 1 + 1e-8) return null;
  const clampedT = Math.min(1, Math.max(0, t));
  return { t: clampedT, u: Math.min(1, Math.max(0, u)), point: lerp(a, b, clampedT) };
}

function insertTJunctions(lines: LngLat[][], toleranceM: number): MarkedLine[] {
  const marked: MarkedLine[] = lines.map((coords) => ({
    coords: coords.map(clone),
    junction: coords.map((_, index) => index === 0 || index === coords.length - 1),
  }));
  splitProperCrossings(marked);
  const endpoints = marked.flatMap((line) => [line.coords[0], line.coords[line.coords.length - 1]]);
  for (const line of marked) {
    for (let i = 0; i < line.coords.length; i++) {
      if (line.junction[i]) continue;
      if (endpoints.some((endpoint) => haversineM(line.coords[i], endpoint) <= toleranceM)) {
        line.junction[i] = true;
      }
    }
  }
  for (const line of marked) {
    const splits: Array<{ seg: number; t: number; point: LngLat }> = [];
    for (const endpoint of endpoints) {
      if (haversineM(endpoint, line.coords[0]) <= toleranceM) continue;
      if (haversineM(endpoint, line.coords[line.coords.length - 1]) <= toleranceM) continue;
      const hit = closestOnPolyline(endpoint, line.coords);
      if (!hit || hit.distanceM > toleranceM || hit.t <= 0.001 || hit.t >= 0.999) continue;
      let nearVertex = false;
      for (let i = 0; i < line.coords.length; i++) {
        if (haversineM(line.coords[i], hit.point) > 1.5) continue;
        line.junction[i] = true;
        nearVertex = true;
        break;
      }
      if (nearVertex) continue;
      if (splits.some((split) => haversineM(split.point, hit.point) <= 1.5)) continue;
      splits.push(hit);
    }
    applyJunctionSplits(line, splits);
  }
  markCoincidentVertices(marked, toleranceM);
  return marked;
}

/** A crossing in the middle of both roads becomes a shared junction. */
function splitProperCrossings(lines: MarkedLine[]): void {
  if (lines.length < 2 || lines[0].coords.length === 0) return;
  const origin = lines[0].coords[0];
  const cell = 50;
  interface Seg {
    li: number;
    si: number;
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
  }
  const segs: Seg[] = [];
  const grid = new Map<string, number[]>();
  for (let li = 0; li < lines.length; li++) {
    for (let si = 0; si < lines[li].coords.length - 1; si++) {
      const a = project(lines[li].coords[si], origin);
      const b = project(lines[li].coords[si + 1], origin);
      const seg: Seg = {
        li,
        si,
        minX: Math.min(a.x, b.x),
        minY: Math.min(a.y, b.y),
        maxX: Math.max(a.x, b.x),
        maxY: Math.max(a.y, b.y),
      };
      const index = segs.length;
      segs.push(seg);
      const x0 = Math.floor(seg.minX / cell);
      const x1 = Math.floor(seg.maxX / cell);
      const y0 = Math.floor(seg.minY / cell);
      const y1 = Math.floor(seg.maxY / cell);
      for (let x = x0; x <= x1; x++) {
        for (let y = y0; y <= y1; y++) {
          const key = `${x}:${y}`;
          const list = grid.get(key) ?? [];
          list.push(index);
          grid.set(key, list);
        }
      }
    }
  }
  const seen = new Set<string>();
  const splits: Array<Array<{ seg: number; t: number; point: LngLat }>> = lines.map(() => []);
  for (const indexes of grid.values()) {
    for (let i = 0; i < indexes.length; i++) {
      for (let j = i + 1; j < indexes.length; j++) {
        const left = segs[indexes[i]];
        const right = segs[indexes[j]];
        if (left.li === right.li) continue;
        const id =
          left.li < right.li
            ? `${left.li}:${left.si}:${right.li}:${right.si}`
            : `${right.li}:${right.si}:${left.li}:${left.si}`;
        if (seen.has(id)) continue;
        seen.add(id);
        const hit = segmentIntersectionTU(
          lines[left.li].coords[left.si],
          lines[left.li].coords[left.si + 1],
          lines[right.li].coords[right.si],
          lines[right.li].coords[right.si + 1],
        );
        if (!hit) continue;
        if (hit.t > 0.02 && hit.t < 0.98) {
          splits[left.li].push({ seg: left.si, t: hit.t, point: hit.point });
        }
        if (hit.u > 0.02 && hit.u < 0.98) {
          splits[right.li].push({ seg: right.si, t: hit.u, point: hit.point });
        }
      }
    }
  }
  for (let li = 0; li < lines.length; li++) applyJunctionSplits(lines[li], splits[li]);
}

/** Vertices that already meet, including shared intersection nodes, become one point. */
function markCoincidentVertices(lines: MarkedLine[], toleranceM: number): void {
  if (lines.length < 2 || lines[0].coords.length === 0) return;
  const origin = lines[0].coords[0];
  const cell = Math.max(toleranceM, 0.5);
  const tol2 = toleranceM * toleranceM;
  const grid = new Map<string, Array<{ li: number; vi: number; x: number; y: number }>>();
  for (let li = 0; li < lines.length; li++) {
    for (let vi = 0; vi < lines[li].coords.length; vi++) {
      const point = project(lines[li].coords[vi], origin);
      const cx = Math.floor(point.x / cell);
      const cy = Math.floor(point.y / cell);
      const key = `${cx}:${cy}`;
      const list = grid.get(key) ?? [];
      list.push({ li, vi, x: point.x, y: point.y });
      grid.set(key, list);
    }
  }
  for (let li = 0; li < lines.length; li++) {
    for (let vi = 0; vi < lines[li].coords.length; vi++) {
      const point = project(lines[li].coords[vi], origin);
      const cx = Math.floor(point.x / cell);
      const cy = Math.floor(point.y / cell);
      for (let ox = -1; ox <= 1 && !lines[li].junction[vi]; ox++) {
        for (let oy = -1; oy <= 1; oy++) {
          const bucket = grid.get(`${cx + ox}:${cy + oy}`);
          if (!bucket) continue;
          for (const other of bucket) {
            if (other.li === li) continue;
            const dx = point.x - other.x;
            const dy = point.y - other.y;
            if (dx * dx + dy * dy > tol2) continue;
            lines[li].junction[vi] = true;
            lines[other.li].junction[other.vi] = true;
            break;
          }
          if (lines[li].junction[vi]) break;
        }
      }
    }
  }
}

function applyJunctionSplits(
  line: MarkedLine,
  splits: Array<{ seg: number; t: number; point: LngLat }>,
): void {
  const unique: Array<{ seg: number; t: number; point: LngLat }> = [];
  for (const split of splits) {
    if (unique.some((item) => item.seg === split.seg && Math.abs(item.t - split.t) < 0.02)) continue;
    if (unique.some((item) => haversineM(item.point, split.point) <= 1)) continue;
    unique.push(split);
  }
  unique.sort((left, right) => right.seg - left.seg || right.t - left.t);
  for (const split of unique) {
    if (split.t <= 0.001 || split.t >= 0.999) continue;
    line.coords.splice(split.seg + 1, 0, split.point);
    line.junction.splice(split.seg + 1, 0, true);
  }
}

function buildGraph(lines: MarkedLine[], toleranceM: number): PieceGraph {
  const junctionPoints: LngLat[] = [];
  const junctionOf = lines.map((line) => line.coords.map(() => -1));
  for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
    const line = lines[lineIndex];
    for (let vertex = 0; vertex < line.coords.length; vertex++) {
      if (!line.junction[vertex]) continue;
      junctionOf[lineIndex][vertex] = junctionPoints.length;
      junctionPoints.push(line.coords[vertex]);
    }
  }
  const clustered = clusterPoints(junctionPoints, toleranceM);
  const points = clustered.reps.map(clone);
  const segments: Array<[number, number]> = [];
  for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
    const line = lines[lineIndex];
    let prev = -1;
    for (let vertex = 0; vertex < line.coords.length; vertex++) {
      const junction = junctionOf[lineIndex][vertex];
      let index: number;
      if (junction >= 0) index = clustered.ids[junction];
      else {
        index = points.length;
        points.push(clone(line.coords[vertex]));
      }
      if (prev >= 0 && prev !== index) addUndirected(segments, prev, index);
      prev = index;
    }
  }
  return { points, segments, start: null };
}

interface Chain {
  nodes: number[];
  edges: number[];
}

function buildChains(graph: PieceGraph): Chain[] {
  const n = graph.points.length;
  const adj: number[][] = Array.from({ length: n }, () => []);
  const edgeBetween = new Map<string, number>();
  graph.segments.forEach(([a, b], index) => {
    if (a < 0 || b < 0 || a >= n || b >= n || a === b) return;
    adj[a].push(b);
    adj[b].push(a);
    edgeBetween.set(edgeKey(a, b), index);
  });
  const fixed = new Set<number>();
  for (let i = 0; i < n; i++) {
    if (adj[i].length !== 2) fixed.add(i);
  }
  const seen = new Set<number>();
  for (let i = 0; i < n; i++) {
    if (adj[i].length !== 2 || seen.has(i)) continue;
    // A pure loop has no dead end to anchor. A chain between real junctions
    // must not pick up an extra fixed point at its first bend.
    let current = i;
    let prev = -1;
    const component = [i];
    let cycle = false;
    for (let step = 0; step <= n; step++) {
      const next = adj[current].find((candidate) => candidate !== prev);
      if (next == null) break;
      if (next === i) {
        cycle = true;
        break;
      }
      if (adj[next].length !== 2) break;
      prev = current;
      current = next;
      component.push(current);
    }
    for (const node of component) seen.add(node);
    if (cycle) fixed.add(i);
  }

  const used = new Set<number>();
  const chains: Chain[] = [];
  for (const start of fixed) {
    for (const next of adj[start] ?? []) {
      const firstEdge = edgeBetween.get(edgeKey(start, next));
      if (firstEdge == null || used.has(firstEdge)) continue;
      const nodes = [start];
      const edges: number[] = [];
      let prev = start;
      let current = next;
      let edge = firstEdge;
      for (let step = 0; step <= n; step++) {
        used.add(edge);
        edges.push(edge);
        nodes.push(current);
        if (fixed.has(current)) break;
        const further = adj[current].find((candidate) => candidate !== prev);
        if (further == null) break;
        const furtherEdge = edgeBetween.get(edgeKey(current, further));
        if (furtherEdge == null || used.has(furtherEdge)) break;
        prev = current;
        current = further;
        edge = furtherEdge;
      }
      if (nodes.length >= 2) chains.push({ nodes, edges });
    }
  }
  return chains;
}

function appendChain(
  graph: PieceGraph,
  chain: Chain,
  spacing: number,
  points: LngLat[],
  segments: Array<[number, number]>,
  covers: number[][],
  fixedOut: Map<number, number>,
): void {
  const coords = chain.nodes.map((index) => graph.points[index]);
  const lens: number[] = [];
  const spans: Array<{ index: number; d0: number; d1: number }> = [];
  let total = 0;
  for (let i = 1; i < coords.length; i++) {
    const len = haversineM(coords[i - 1], coords[i]);
    spans.push({ index: chain.edges[i - 1], d0: total, d1: total + len });
    lens.push(len);
    total += len;
  }
  if (!(total > 0.05)) return;
  const sampleD = [0];
  const endGap = Math.min(total * 0.5, spacing * 0.25);
  for (let distance = spacing; distance < total - endGap; distance += spacing) sampleD.push(distance);
  sampleD.push(total);

  const sampleIndex: number[] = [];
  for (let i = 0; i < sampleD.length; i++) {
    const atEnd = i === 0 || i === sampleD.length - 1;
    if (atEnd) {
      const original = i === 0 ? chain.nodes[0] : chain.nodes[chain.nodes.length - 1];
      sampleIndex.push(fixedPoint(original, graph, points, fixedOut));
    } else {
      sampleIndex.push(points.length);
      points.push(pointAtDistance(coords, lens, sampleD[i]));
    }
  }
  for (let i = 1; i < sampleIndex.length; i++) {
    const from = sampleIndex[i - 1];
    const to = sampleIndex[i];
    if (from === to) continue;
    const before = segments.length;
    addUndirected(segments, from, to);
    if (segments.length === before) continue;
    const d0 = sampleD[i - 1];
    const d1 = sampleD[i];
    const last = i === sampleIndex.length - 1;
    const used: number[] = [];
    for (const span of spans) {
      const mid = (span.d0 + span.d1) / 2;
      const starts = mid >= d0 - 1e-6;
      const ends = last ? mid <= d1 + 1e-6 : mid < d1 - 1e-6;
      if (starts && ends) used.push(span.index);
    }
    covers.push(used);
  }
}

function fixedPoint(
  original: number,
  graph: PieceGraph,
  points: LngLat[],
  fixedOut: Map<number, number>,
): number {
  const existing = fixedOut.get(original);
  if (existing != null) return existing;
  const index = points.length;
  points.push(clone(graph.points[original]));
  fixedOut.set(original, index);
  return index;
}

function pointAtDistance(coords: LngLat[], lens: number[], distance: number): LngLat {
  let walked = 0;
  for (let i = 0; i < lens.length; i++) {
    const len = lens[i];
    if (walked + len >= distance - 1e-6) {
      const t = len === 0 ? 0 : (distance - walked) / len;
      return lerp(coords[i], coords[i + 1], Math.min(1, Math.max(0, t)));
    }
    walked += len;
  }
  return clone(coords[coords.length - 1]);
}

function joinDanglingEnds(graph: PieceGraph, toleranceM: number): PieceGraph {
  const points = graph.points.map(clone);
  const segments = graph.segments.map(([a, b]) => [a, b] as [number, number]);
  for (let pass = 0; pass < 12; pass++) {
    const degree = new Array<number>(points.length).fill(0);
    for (const [a, b] of segments) {
      degree[a] += 1;
      degree[b] += 1;
    }
    let best: { from: number; seg: number; distance: number; t: number; point: LngLat } | null = null;
    for (let i = 0; i < points.length; i++) {
      if (degree[i] !== 1) continue;
      for (let s = 0; s < segments.length; s++) {
        const [a, b] = segments[s];
        if (a === i || b === i) continue;
        const hit = closestPointOnSegment(points[i], points[a], points[b]);
        if (hit.distanceM > toleranceM) continue;
        if (!best || hit.distanceM < best.distance) {
          best = { from: i, seg: s, distance: hit.distanceM, t: hit.t, point: hit.point };
        }
      }
    }
    if (!best) break;
    const [a, b] = segments[best.seg];
    if (best.t <= 0.05 || best.t >= 0.95) {
      const target = best.t < 0.5 ? a : b;
      if (target === best.from || segments.some(([x, y]) => (x === best.from && y === target) || (x === target && y === best.from))) {
        break;
      }
      addUndirected(segments, best.from, target);
      continue;
    }
    const index = points.length;
    points.push(clone(best.point));
    segments.splice(best.seg, 1);
    addUndirected(segments, a, index);
    addUndirected(segments, index, b);
    addUndirected(segments, best.from, index);
  }
  return { points, segments, start: graph.start };
}

function subdivideLongEdges(graph: PieceGraph, maxLen: number): PieceGraph {
  const points = graph.points.map(clone);
  const segments: Array<[number, number]> = [];
  for (const [a, b] of graph.segments) {
    const start = graph.points[a];
    const end = graph.points[b];
    if (!start || !end) continue;
    const len = haversineM(start, end);
    if (!(len > maxLen)) {
      addUndirected(segments, a, b);
      continue;
    }
    const pieces = Math.ceil(len / maxLen);
    let prev = a;
    for (let i = 1; i < pieces; i++) {
      const index = points.length;
      points.push(lerp(start, end, i / pieces));
      addUndirected(segments, prev, index);
      prev = index;
    }
    addUndirected(segments, prev, b);
  }
  return { points, segments, start: graph.start };
}

function clusterPoints(pts: LngLat[], toleranceM: number): { ids: number[]; reps: LngLat[] } {
  if (pts.length === 0) return { ids: [], reps: [] };
  const parent = pts.map((_, index) => index);
  const find = (index: number): number => {
    let root = index;
    while (parent[root] !== root) root = parent[root];
    let cursor = index;
    while (parent[cursor] !== root) {
      const next = parent[cursor];
      parent[cursor] = root;
      cursor = next;
    }
    return root;
  };
  const unite = (a: number, b: number): void => {
    const pa = find(a);
    const pb = find(b);
    if (pa !== pb) parent[pa] = pb;
  };
  const origin = pts[0];
  const local = pts.map((point) => project(point, origin));
  const cell = Math.max(toleranceM, 0.5);
  const grid = new Map<string, number[]>();
  const tol2 = toleranceM * toleranceM;
  for (let i = 0; i < pts.length; i++) {
    const cx = Math.floor(local[i].x / cell);
    const cy = Math.floor(local[i].y / cell);
    for (let ox = -1; ox <= 1; ox++) {
      for (let oy = -1; oy <= 1; oy++) {
        const bucket = grid.get(`${cx + ox}:${cy + oy}`);
        if (!bucket) continue;
        for (const j of bucket) {
          const dx = local[i].x - local[j].x;
          const dy = local[i].y - local[j].y;
          if (dx * dx + dy * dy <= tol2) unite(i, j);
        }
      }
    }
    const key = `${cx}:${cy}`;
    const list = grid.get(key);
    if (list) list.push(i);
    else grid.set(key, [i]);
  }
  const rootToId = new Map<number, number>();
  const groups: number[][] = [];
  const ids = new Array<number>(pts.length).fill(0);
  for (let i = 0; i < pts.length; i++) {
    const root = find(i);
    let id = rootToId.get(root);
    if (id == null) {
      id = groups.length;
      rootToId.set(root, id);
      groups.push([]);
    }
    ids[i] = id;
    groups[id].push(i);
  }
  const reps = groups.map((members) => {
    let x = 0;
    let y = 0;
    for (const index of members) {
      x += local[index].x;
      y += local[index].y;
    }
    return unproject(x / members.length, y / members.length, origin);
  });
  return { ids, reps };
}

function closestOnPolyline(
  point: LngLat,
  line: LngLat[],
): { seg: number; t: number; point: LngLat; distanceM: number } | null {
  let best: { seg: number; t: number; point: LngLat; distanceM: number } | null = null;
  for (let i = 1; i < line.length; i++) {
    const hit = closestPointOnSegment(point, line[i - 1], line[i]);
    if (!best || hit.distanceM < best.distanceM) {
      best = { seg: i - 1, t: hit.t, point: hit.point, distanceM: hit.distanceM };
    }
  }
  return best;
}

function dedupeExact(lines: LngLat[][]): LngLat[][] {
  const seen = new Set<string>();
  const out: LngLat[][] = [];
  for (const line of lines) {
    const key = lineKey(line);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(line);
  }
  return out;
}

function lineKey(line: LngLat[]): string {
  const round = (point: LngLat) => `${Math.round(point[0] * 1e5)},${Math.round(point[1] * 1e5)}`;
  const forward = line.map(round).join(";");
  const reverse = [...line].reverse().map(round).join(";");
  return forward < reverse ? forward : reverse;
}

function cleanLine(line: readonly LngLat[]): LngLat[] {
  const out: LngLat[] = [];
  for (const point of line) {
    if (!Number.isFinite(point[0]) || !Number.isFinite(point[1])) continue;
    const prev = out[out.length - 1];
    if (prev && haversineM(prev, point) < 0.4) continue;
    out.push(clone(point));
  }
  return out;
}

function onRing(point: LngLat, ring: LngLat[], epsilonM: number): boolean {
  for (let i = 0; i < ring.length; i++) {
    const hit = closestPointOnSegment(point, ring[i], ring[(i + 1) % ring.length]);
    if (hit.distanceM <= epsilonM) return true;
  }
  return false;
}

function stripRing(ring: readonly LngLat[]): LngLat[] {
  const out = ring.map(clone);
  if (out.length >= 2 && haversineM(out[0], out[out.length - 1]) < 0.5) out.pop();
  return out;
}

function nearestIndex(points: readonly LngLat[], point: LngLat, toleranceM: number): number {
  let best = -1;
  let bestDistance = toleranceM;
  for (let i = 0; i < points.length; i++) {
    const distance = haversineM(points[i], point);
    if (distance <= bestDistance) {
      bestDistance = distance;
      best = i;
    }
  }
  return best;
}

/**
 * Duplicate check for one segment list. A linear scan made every erase of a
 * long graph rebuild in time proportional to the square of the line count.
 */
const undirectedEdges = new WeakMap<Array<[number, number]>, Set<string>>();

function addUndirected(segments: Array<[number, number]>, a: number, b: number): void {
  if (a === b || a < 0 || b < 0) return;
  const lo = Math.min(a, b);
  const hi = Math.max(a, b);
  let seen = undirectedEdges.get(segments);
  if (!seen) {
    seen = new Set();
    for (const [x, y] of segments) {
      if (x === y) continue;
      const xlo = x < y ? x : y;
      const xhi = x < y ? y : x;
      seen.add(`${xlo}:${xhi}`);
    }
    undirectedEdges.set(segments, seen);
  }
  const key = `${lo}:${hi}`;
  if (seen.has(key)) return;
  seen.add(key);
  segments.push([lo, hi]);
}

function edgeKey(a: number, b: number): string {
  return a < b ? `${a}:${b}` : `${b}:${a}`;
}

function project(point: LngLat, origin: LngLat): Local {
  const lngScale = LAT_M * Math.max(0.01, Math.cos((origin[1] * Math.PI) / 180));
  return {
    x: (point[0] - origin[0]) * lngScale,
    y: (point[1] - origin[1]) * LAT_M,
  };
}

function unproject(x: number, y: number, origin: LngLat): LngLat {
  const lngScale = LAT_M * Math.max(0.01, Math.cos((origin[1] * Math.PI) / 180));
  return [origin[0] + x / lngScale, origin[1] + y / LAT_M];
}

function lerp(a: LngLat, b: LngLat, t: number): LngLat {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
}

function clone(point: LngLat): LngLat {
  return [point[0], point[1]];
}
